import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Paper,
    Breadcrumbs,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    Save as SaveIcon,
    Settings as SettingsIcon,
    NavigateNext as NavigateNextIcon,
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import { useNotification } from '../../../components/Notification';
import Switch from '../../../components/Switch';
import Input from '../../../components/Input';
import { hasPermission } from '../../../utils/permissions';
import VNeIDSettings from './VNeIDSettings';
import {
    fetchSettings,
    updateSettings,
    updateLocalSettingValue,
    selectSettings,
    selectSettingsLoading,
    selectSettingsError,
    selectSettingsSuccessMessage,
    clearError,
    clearSuccessMessage,
} from './slice';
import {
    SETTINGS_VALIDATION_RULES,
    getPositiveIntegerRangeError,
    validateFileExtension,
    getFileExtensionError,
    validateNotificationRecipient,
    getNotificationRecipientError,
    validateSettingTextField,
    getSettingTextFieldError,
    validateURL,
    getURLError,
    validateQueryType,
    getQueryTypeError,
    ALLOWED_QUERY_TYPES,
} from '../../../utils/validation';
import './Settings.css';

function Settings() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const settings = useSelector(selectSettings);
    const loading = useSelector(selectSettingsLoading);
    const error = useSelector(selectSettingsError);
    const successMessage = useSelector(selectSettingsSuccessMessage);

    const [expandedPanel, setExpandedPanel] = useState('');
    // Validation errors state: { category_key: errorMessage }
    const [validationErrors, setValidationErrors] = useState({});
    // Store original settings to compare for changes
    const [originalSettings, setOriginalSettings] = useState([]);
    // Password confirmation dialog state
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwordValue, setPasswordValue] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [pendingSettingsPayload, setPendingSettingsPayload] = useState(null);

    // Subordinate levels dialog state
    const [subordinateLevelDialogOpen, setSubordinateLevelDialogOpen] = useState(false);
    const [subordinateLevelEditMode, setSubordinateLevelEditMode] = useState(false);
    const [subordinateLevelEditIndex, setSubordinateLevelEditIndex] = useState(-1);
    const [subordinateLevelFormData, setSubordinateLevelFormData] = useState({
        code: '',
        name: '',
        description: '',
    });
    const [subordinateLevelCategory, setSubordinateLevelCategory] = useState('');
    const [subordinateLevelKey, setSubordinateLevelKey] = useState('');
    const [subordinateLevelDeleteDialogOpen, setSubordinateLevelDeleteDialogOpen] = useState(false);
    const [subordinateLevelToDelete, setSubordinateLevelToDelete] = useState(null);

    // Check permissions
    const canEditSettings = hasPermission('setting', 'editSetting');

    // Fetch settings on mount
    useEffect(() => {
        dispatch(fetchSettings());
        // Clear validation errors when settings are loaded
        setValidationErrors({});
    }, [dispatch]);

    // Store original settings when they are loaded
    useEffect(() => {
        if (settings.length > 0 && originalSettings.length === 0) {
            // Deep clone settings to store original values
            setOriginalSettings(JSON.parse(JSON.stringify(settings)));
        }
    }, [settings, originalSettings]);

    // Show error notification
    useEffect(() => {
        if (error) {
            if (passwordDialogOpen) {
                const translatedInvalidPassword = t('settings.validation.invalidPassword');
                if (error === 'Password confirmation is invalid') {
                    const displayMessage =
                        translatedInvalidPassword && translatedInvalidPassword !== 'settings.validation.invalidPassword'
                            ? translatedInvalidPassword
                            : error;
                    setPasswordError(displayMessage);
                } else {
                    setPasswordError(error);
                }
            }
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification, passwordDialogOpen, t]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            // Translate success message if it's the default English message
            const translatedMessage = successMessage === 'Settings updated successfully' 
                ? t('settings.success.updated')
                : successMessage;
            showNotification(translatedMessage, 'success');
            dispatch(clearSuccessMessage());
            // Clear validation errors on successful save
            setValidationErrors({});
            // Reset password confirmation dialog
            setPasswordDialogOpen(false);
            setPasswordValue('');
            setPasswordError('');
            setPendingSettingsPayload(null);
            // Update original settings after successful save
            setOriginalSettings(JSON.parse(JSON.stringify(settings)));
        }
    }, [successMessage, dispatch, showNotification, settings, t]);

    // Handle accordion panel expansion
    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedPanel(isExpanded ? panel : '');
    };

    // Handle text input change - allow free typing, validate on blur
    const handleTextChange = (category, key, value, type) => {
        // Update the value - allow any input
        dispatch(updateLocalSettingValue({ category, key, value }));

        const errorKey = `${category}_${key}`;

        // Real-time validation for number fields
        if (type === 'number') {
            const validationRule = SETTINGS_VALIDATION_RULES[key];
            
            if (value !== '' && value !== null && value !== undefined) {
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                
                // Check if it's a valid number
                if (isNaN(numValue)) {
                    // Invalid number format - show error
                    setValidationErrors((prev) => ({
                        ...prev,
                        [errorKey]: t('settings.validation.mustBePositive') || 'Giá trị phải là số nguyên dương',
                    }));
                    return;
                }

                // Check if it's an integer
                if (!Number.isInteger(numValue)) {
                    setValidationErrors((prev) => ({
                        ...prev,
                        [errorKey]: t('settings.validation.mustBeInteger') || 'Giá trị phải là số nguyên',
                    }));
                    return;
                }

                // Validate based on rules
                if (validationRule) {
                    const error = getPositiveIntegerRangeError(value, validationRule.min, validationRule.max, t);
                    if (error) {
                        setValidationErrors((prev) => ({
                            ...prev,
                            [errorKey]: error,
                        }));
                        return;
                    }
                } else {
                    // For number fields without specific rules, check if it's > 0
                    if (numValue <= 0) {
                        setValidationErrors((prev) => ({
                            ...prev,
                            [errorKey]: t('settings.validation.mustBePositive') || 'Giá trị phải là số nguyên dương',
                        }));
                        return;
                    }
                }

                // Clear error if valid
                setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            } else {
                // Clear validation error when field is empty (allow them to clear and re-enter)
                setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            }
        } else if (type === 'text') {
            // Real-time validation for text fields (check max length and URL)
            if (value && value.trim()) {
                let error = '';
                
                // Special validation for URL fields
                if (key === 'vneid_endpoint_url') {
                    error = getURLError(value, t);
                } else {
                    error = getSettingTextFieldError(value, key, t);
                }
                
                if (error) {
                    setValidationErrors((prev) => ({
                        ...prev,
                        [errorKey]: error,
                    }));
                } else {
                    // Clear error if valid
                    setValidationErrors((prev) => {
                        const newErrors = { ...prev };
                        delete newErrors[errorKey];
                        return newErrors;
                    });
                }
            } else {
                // Clear validation error when field is empty
                setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            }
        } else {
            // Clear validation error when user starts typing (for other fields)
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    // Validate text field on blur (when user leaves the field)
    const handleTextBlur = (category, key, value) => {
        const errorKey = `${category}_${key}`;

        if (value && value.trim()) {
            let error = '';
            
            // Special validation for URL fields
            if (key === 'vneid_endpoint_url') {
                error = getURLError(value, t);
            } else {
                error = getSettingTextFieldError(value, key, t);
            }
            
            if (error) {
                setValidationErrors((prev) => ({
                    ...prev,
                    [errorKey]: error,
                }));
            } else {
                // Clear error if valid
                setValidationErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[errorKey];
                    return newErrors;
                });
            }
        }
    };

    // Validate number field on blur (when user leaves the field)
    const handleNumberBlur = (category, key, value) => {
        const errorKey = `${category}_${key}`;
        const validationRule = SETTINGS_VALIDATION_RULES[key];

        if (validationRule) {
            const error = getPositiveIntegerRangeError(value, validationRule.min, validationRule.max, t);
            if (error) {
                setValidationErrors((prev) => ({
                    ...prev,
                    [errorKey]: error,
                }));
            }
        } else if (value !== '' && value !== null && value !== undefined) {
            // For number fields without specific rules, check if it's a valid non-negative integer
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            if (isNaN(numValue) || !Number.isInteger(numValue) || numValue < 0) {
                setValidationErrors((prev) => ({
                    ...prev,
                    [errorKey]: t('settings.validation.mustBeNonNegative') || 'Giá trị phải là số nguyên không âm (từ 0 trở lên)',
                }));
            }
        }
    };

    // Handle boolean switch change
    const handleBoolChange = (category, key, checked) => {
        dispatch(updateLocalSettingValue({ category, key, value: checked.toString() }));
    };

    // Handle adding new value to multiple setting with validation
    const handleAddMultipleValue = (category, key, newValue) => {
        const categoryData = settings.find((cat) => cat.category === category);
        const setting = categoryData?.settings.find((s) => s.key === key);
        if (!setting || !newValue || !newValue.trim()) {
            return;
        }

        const trimmedValue = newValue.trim();
        const errorKey = `${category}_${key}_new`;
        let validationError = '';

        // Validate based on setting key
        if (key === 'file_allowed_extensions') {
            if (!validateFileExtension(trimmedValue)) {
                validationError = getFileExtensionError(trimmedValue, t);
            }
        } else if (key === 'notification_users') {
            if (!validateNotificationRecipient(trimmedValue)) {
                validationError = getNotificationRecipientError(trimmedValue, t);
            }
        } else if (key === 'vneid_query_allowed_types') {
            if (!validateQueryType(trimmedValue)) {
                validationError = getQueryTypeError(trimmedValue, t);
            }
        }

        // If validation fails, show error and don't add
        if (validationError) {
            setValidationErrors((prev) => ({
                ...prev,
                [errorKey]: validationError,
            }));
            return;
        }

        // Clear error and add value
        setValidationErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[errorKey];
            return newErrors;
        });

        const newValues = [...setting.values, trimmedValue];
        dispatch(updateLocalSettingValue({ category, key, value: newValues }));
    };

    // Handle removing value from multiple setting
    const handleRemoveMultipleValue = (category, key, index) => {
        const categoryData = settings.find((cat) => cat.category === category);
        const setting = categoryData?.settings.find((s) => s.key === key);
        if (setting && setting.values.length > 0) {
            const newValues = setting.values.filter((_, i) => i !== index);
            dispatch(updateLocalSettingValue({ category, key, value: newValues }));

            // Clear validation error for removed value
            const valueErrorKey = `${category}_${key}_${index}`;
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[valueErrorKey];
                // Also reindex any errors for values after the removed one
                Object.keys(newErrors).forEach((errorKey) => {
                    const match = errorKey.match(new RegExp(`^${category}_${key}_(\\d+)$`));
                    if (match) {
                        const errorIndex = parseInt(match[1], 10);
                        if (errorIndex > index) {
                            // Move error to new index
                            const newErrorKey = `${category}_${key}_${errorIndex - 1}`;
                            newErrors[newErrorKey] = newErrors[errorKey];
                            delete newErrors[errorKey];
                        }
                    }
                });
                return newErrors;
            });
        }
    };

    // Validate all settings before save
    const validateAllSettings = () => {
        const errors = {};
        let hasErrors = false;

        settings.forEach((categoryData) => {
            categoryData.settings.forEach((setting) => {
                const { category, key, type, values } = setting;
                const errorKey = `${category}_${key}`;

                // Validate text fields
                if (type === 'text') {
                    if (values[0] && values[0].trim()) {
                        let error = '';
                        
                        // Special validation for URL fields
                        if (key === 'vneid_endpoint_url') {
                            error = getURLError(values[0], t);
                        } else {
                            error = getSettingTextFieldError(values[0], key, t);
                        }
                        
                        if (error) {
                            errors[errorKey] = error;
                            hasErrors = true;
                        }
                    }
                }

                // Validate number fields
                if (type === 'number') {
                    const validationRule = SETTINGS_VALIDATION_RULES[key];
                    if (validationRule) {
                        const error = getPositiveIntegerRangeError(values[0], validationRule.min, validationRule.max, t);
                        if (error) {
                            errors[errorKey] = error;
                            hasErrors = true;
                        }
                    } else if (values[0] !== '' && values[0] !== null && values[0] !== undefined) {
                        // For number fields without specific rules, check if it's a valid non-negative integer
                        const numValue = typeof values[0] === 'string' ? parseFloat(values[0]) : values[0];
                        if (isNaN(numValue) || !Number.isInteger(numValue) || numValue < 0) {
                            errors[errorKey] = t('settings.validation.mustBeNonNegative') || 'Giá trị phải là số nguyên không âm (từ 0 trở lên)';
                            hasErrors = true;
                        }
                    }
                }

                // Validate multiple fields
                if (type === 'multiple') {
                    if (key === 'file_allowed_extensions') {
                        values.forEach((value, index) => {
                            if (!validateFileExtension(value)) {
                                errors[`${errorKey}_${index}`] = getFileExtensionError(value, t);
                                hasErrors = true;
                            }
                        });
                    } else if (key === 'notification_users') {
                        values.forEach((value, index) => {
                            if (!validateNotificationRecipient(value)) {
                                errors[`${errorKey}_${index}`] = getNotificationRecipientError(value, t);
                                hasErrors = true;
                            }
                        });
                    } else if (key === 'vneid_query_allowed_types') {
                        values.forEach((value, index) => {
                            if (!validateQueryType(value)) {
                                errors[`${errorKey}_${index}`] = getQueryTypeError(value, t);
                                hasErrors = true;
                            }
                        });
                    }
                }
            });
        });

        setValidationErrors(errors);
        return !hasErrors;
    };

    // Compare current settings with original to detect changes
    const hasSettingsChanged = () => {
        if (originalSettings.length === 0) return false;

        // Compare each setting value
        for (let i = 0; i < settings.length; i++) {
            const category = settings[i];
            const originalCategory = originalSettings.find(c => c.category === category.category);
            
            if (!originalCategory) return true;

            for (let j = 0; j < category.settings.length; j++) {
                const setting = category.settings[j];
                const originalSetting = originalCategory.settings.find(s => s.key === setting.key);
                
                if (!originalSetting) return true;

                // Compare values (handle arrays and strings)
                const currentValues = JSON.stringify(setting.values || []);
                const originalValues = JSON.stringify(originalSetting.values || []);
                
                if (currentValues !== originalValues) {
                    return true;
                }
            }
        }

        return false;
    };

    // Handle save button click
    const handleSave = () => {
        // Check if there are any changes
        if (!hasSettingsChanged()) {
            showNotification(
                t('settings.noChanges') || 'Không có thay đổi nào để lưu.',
                'info'
            );
            return;
        }

        // Validate all settings first
        if (!validateAllSettings()) {
            showNotification(
                t('settings.validation.invalidConfiguration') || 'Cấu hình không hợp lệ. Vui lòng kiểm tra lại các trường đã nhập.',
                'error'
            );
            return;
        }

        // Transform settings data to API format
        const settingsData = settings.flatMap((category) =>
            category.settings.map((setting) => ({
                key: setting.key,
                values: setting.values,
            }))
        );

        setPendingSettingsPayload(settingsData);
        setPasswordValue('');
        setPasswordError('');
        setPasswordDialogOpen(true);
    };

    const handleConfirmSaveWithPassword = () => {
        if (loading || !pendingSettingsPayload) {
            return;
        }

        if (!passwordValue || !passwordValue.trim()) {
            setPasswordError(t('settings.validation.passwordRequired') || 'Vui lòng nhập mật khẩu');
            return;
        }

        dispatch(updateSettings({
            password: passwordValue,
            settings: pendingSettingsPayload,
        }));
    };

    const handleClosePasswordDialog = () => {
        if (loading) {
            return;
        }
        setPasswordDialogOpen(false);
        setPasswordValue('');
        setPasswordError('');
        setPendingSettingsPayload(null);
    };

    // Subordinate levels handlers
    const handleOpenSubordinateLevelDialog = (category, key, editMode = false, index = -1, item = null) => {
        setSubordinateLevelCategory(category);
        setSubordinateLevelKey(key);
        setSubordinateLevelEditMode(editMode);
        setSubordinateLevelEditIndex(index);
        if (editMode && item) {
            setSubordinateLevelFormData({
                code: item.code || '',
                name: item.name || '',
                description: item.description || '',
            });
        } else {
            setSubordinateLevelFormData({
                code: '',
                name: '',
                description: '',
            });
        }
        setSubordinateLevelDialogOpen(true);
    };

    const handleCloseSubordinateLevelDialog = () => {
        setSubordinateLevelDialogOpen(false);
        setSubordinateLevelFormData({
            code: '',
            name: '',
            description: '',
        });
        setSubordinateLevelEditMode(false);
        setSubordinateLevelEditIndex(-1);
        setSubordinateLevelCategory('');
        setSubordinateLevelKey('');
    };

    const handleSubordinateLevelFormChange = (field) => (event) => {
        setSubordinateLevelFormData({
            ...subordinateLevelFormData,
            [field]: event.target.value,
        });
    };

    const handleConfirmSubordinateLevel = () => {
        const categoryData = settings.find((cat) => cat.category === subordinateLevelCategory);
        const setting = categoryData?.settings.find((s) => s.key === subordinateLevelKey);
        if (!setting) return;

        let newValues;
        if (subordinateLevelEditMode && subordinateLevelEditIndex >= 0) {
            // Edit existing item
            newValues = [...setting.values];
            newValues[subordinateLevelEditIndex] = { ...subordinateLevelFormData };
        } else {
            // Add new item
            newValues = [...setting.values, { ...subordinateLevelFormData }];
        }

        dispatch(updateLocalSettingValue({
            category: subordinateLevelCategory,
            key: subordinateLevelKey,
            value: newValues,
        }));

        handleCloseSubordinateLevelDialog();
    };

    const handleOpenDeleteSubordinateLevelDialog = (category, key, index, item) => {
        setSubordinateLevelCategory(category);
        setSubordinateLevelKey(key);
        setSubordinateLevelToDelete({ index, item });
        setSubordinateLevelDeleteDialogOpen(true);
    };

    const handleCloseDeleteSubordinateLevelDialog = () => {
        setSubordinateLevelDeleteDialogOpen(false);
        setSubordinateLevelToDelete(null);
        setSubordinateLevelCategory('');
        setSubordinateLevelKey('');
    };

    const handleConfirmDeleteSubordinateLevel = () => {
        if (!subordinateLevelToDelete) return;

        const categoryData = settings.find((cat) => cat.category === subordinateLevelCategory);
        const setting = categoryData?.settings.find((s) => s.key === subordinateLevelKey);
        if (!setting) return;

        const newValues = setting.values.filter((_, i) => i !== subordinateLevelToDelete.index);
        dispatch(updateLocalSettingValue({
            category: subordinateLevelCategory,
            key: subordinateLevelKey,
            value: newValues,
        }));

        handleCloseDeleteSubordinateLevelDialog();
    };

    const isSubordinateLevelFormValid = () => {
        return subordinateLevelFormData.code && subordinateLevelFormData.code.trim() &&
               subordinateLevelFormData.name && subordinateLevelFormData.name.trim();
    };

    // Render setting input based on type
    const renderSettingInput = (category, setting) => {
        const { key, title, description, type, values } = setting;

        // Special handling for VNeID SDK category
        if (category === 'VNeID SDK') {
            return <VNeIDSettings />;
        }

        switch (type) {
            case 'text':
                const textErrorKey = `${category}_${key}`;
                const textError = validationErrors[textErrorKey] || '';
                const textLabel = title ? t(title) : '';
                return (
                    <Box>
                        <Input
                            {...(textLabel && textLabel.trim() ? { label: textLabel } : {})}
                            value={values[0] || ''}
                            onChange={(e) => handleTextChange(category, key, e.target.value, 'text')}
                            onBlur={(e) => handleTextBlur(category, key, e.target.value)}
                            disabled={!canEditSettings}
                            id={`setting-${key}`}
                            name={`setting-${key}`}
                            data-testid={`settings-input-${key}`}
                            error={!!textError}
                            helperText={textError}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t(description)}
                        </Typography>
                    </Box>
                );

            case 'number':
                const numberErrorKey = `${category}_${key}`;
                const numberError = validationErrors[numberErrorKey] || '';
                const numberLabel = title ? t(title) : '';
                return (
                    <Box>
                        <Input
                            {...(numberLabel && numberLabel.trim() ? { label: numberLabel } : {})}
                            type="number"
                            value={values[0] || ''}
                            onChange={(e) => handleTextChange(category, key, e.target.value, 'number')}
                            onBlur={(e) => handleNumberBlur(category, key, e.target.value)}
                            disabled={!canEditSettings}
                            id={`setting-${key}`}
                            name={`setting-${key}`}
                            data-testid={`settings-input-${key}`}
                            error={!!numberError}
                            helperText={numberError}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t(description)}
                        </Typography>
                    </Box>
                );

            case 'bool':
                return (
                    <Box>
                        <Switch
                            label={t(title)}
                            checked={values[0] === 'true' || values[0] === true}
                            onChange={(e) => handleBoolChange(category, key, e.target.checked)}
                            disabled={!canEditSettings}
                            id={`setting-${key}`}
                            name={`setting-${key}`}
                            data-testid={`settings-switch-${key}`}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t(description)}
                        </Typography>
                    </Box>
                );

            case 'select':
                const selectLabel = title ? t(title) : '';
                return (
                    <Box>
                        <FormControl fullWidth>
                            {selectLabel && selectLabel.trim() && <InputLabel id={`setting-${key}-label`}>{selectLabel}</InputLabel>}
                            <Select
                                {...(selectLabel && selectLabel.trim()
                                    ? {
                                          labelId: `setting-${key}-label`,
                                          label: selectLabel,
                                      }
                                    : {})}
                                id={`setting-${key}`}
                                value={values[0] || ''}
                                onChange={(e) => handleTextChange(category, key, e.target.value)}
                                disabled={!canEditSettings}
                                data-testid={`settings-select-${key}`}
                            >
                                {setting.options?.map((option) => (
                                    <MenuItem key={option.key} value={option.key}>
                                        {option.value}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {t(description)}
                        </Typography>
                    </Box>
                );

            case 'multiple':
                const multipleErrorKey = `${category}_${key}_new`;
                const multipleError = validationErrors[multipleErrorKey] || '';
                // Check for errors in existing values
                const existingValueErrors = values
                    .map((value, index) => {
                        const valueErrorKey = `${category}_${key}_${index}`;
                        return validationErrors[valueErrorKey];
                    })
                    .filter(Boolean);

                return (
                    <Box>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                            {t(title)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 2 }}>
                            {t(description)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {values.map((value, index) => {
                                const valueErrorKey = `${category}_${key}_${index}`;
                                const valueError = validationErrors[valueErrorKey];
                                return (
                                    <Chip
                                        key={index}
                                        label={value}
                                        onDelete={canEditSettings ? () => handleRemoveMultipleValue(category, key, index) : undefined}
                                        variant="outlined"
                                        size="medium"
                                        color={valueError ? 'error' : 'default'}
                                        sx={valueError ? { borderColor: 'error.main' } : {}}
                                    />
                                );
                            })}
                        </Box>
                        {existingValueErrors.length > 0 && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                                {existingValueErrors[0]}
                            </Typography>
                        )}
                        {canEditSettings && (
                            <Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Input
                                            placeholder={t('settings.addValue')}
                                            id={`setting-${key}-new`}
                                            name={`setting-${key}-new`}
                                            data-testid={`settings-input-${key}-new`}
                                            error={!!multipleError}
                                            helperText={multipleError}
                                            fullWidth={true}
                                            onChange={(e) => {
                                                const inputValue = e.target.value;
                                                const errorKey = `${category}_${key}_new`;
                                                
                                                // Real-time validation for file extensions
                                                if (key === 'file_allowed_extensions' && inputValue.trim()) {
                                                    if (!validateFileExtension(inputValue)) {
                                                        setValidationErrors((prev) => ({
                                                            ...prev,
                                                            [errorKey]: getFileExtensionError(inputValue, t),
                                                        }));
                                                    } else {
                                                        // Clear error if valid
                                                        setValidationErrors((prev) => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors[errorKey];
                                                            return newErrors;
                                                        });
                                                    }
                                                } else if (key === 'notification_users' && inputValue.trim()) {
                                                    // Real-time validation for notification recipients
                                                    if (!validateNotificationRecipient(inputValue)) {
                                                        setValidationErrors((prev) => ({
                                                            ...prev,
                                                            [errorKey]: getNotificationRecipientError(inputValue, t),
                                                        }));
                                                    } else {
                                                        // Clear error if valid
                                                        setValidationErrors((prev) => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors[errorKey];
                                                            return newErrors;
                                                        });
                                                    }
                                                } else if (key === 'vneid_query_allowed_types' && inputValue.trim()) {
                                                    // Real-time validation for query types
                                                    if (!validateQueryType(inputValue)) {
                                                        setValidationErrors((prev) => ({
                                                            ...prev,
                                                            [errorKey]: getQueryTypeError(inputValue, t),
                                                        }));
                                                    } else {
                                                        // Clear error if valid
                                                        setValidationErrors((prev) => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors[errorKey];
                                                            return newErrors;
                                                        });
                                                    }
                                                } else {
                                                    // Clear validation error when user starts typing (for other fields)
                                                    if (validationErrors[errorKey]) {
                                                        setValidationErrors((prev) => {
                                                            const newErrors = { ...prev };
                                                            delete newErrors[errorKey];
                                                            return newErrors;
                                                        });
                                                    }
                                                }
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && e.target.value.trim()) {
                                                    handleAddMultipleValue(category, key, e.target.value);
                                                    // Clear input after adding
                                                    const input = e.target;
                                                    if (input) {
                                                        input.value = '';
                                                    }
                                                }
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    height: '40px'
                                                }
                                            }}
                                        />
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const input = document.getElementById(`setting-${key}-new`);
                                            if (input && input.value.trim()) {
                                                handleAddMultipleValue(category, key, input.value);
                                                input.value = '';
                                            }
                                        }}
                                        id={`add-${key}`}
                                        name={`add-${key}`}
                                        data-testid={`settings-button-add-${key}`}
                                        sx={{ 
                                            alignSelf: 'flex-start',
                                            mt: 0,
                                            height: '40px',
                                            minHeight: '40px'
                                        }}
                                    >
                                        {t('settings.add')}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                );

            case 'subordinate_levels':
                return (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                                <Typography variant="body2" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AccountTreeIcon color="primary" fontSize="small" />
                                    {t(title)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t(description)}
                                </Typography>
                            </Box>
                            {canEditSettings && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenSubordinateLevelDialog(category, key)}
                                    id={`add-${key}`}
                                    name={`add-${key}`}
                                    data-testid={`settings-button-add-${key}`}
                                >
                                    {t('settings.subordinateLevels.add')}
                                </Button>
                            )}
                        </Box>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('settings.subordinateLevels.table.code')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('settings.subordinateLevels.table.name')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>{t('settings.subordinateLevels.table.description')}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: 100 }} align="center">
                                            {t('settings.subordinateLevels.table.actions')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(!values || values.length === 0) ? (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                                {t('settings.subordinateLevels.noData')}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        values.map((item, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{item.code}</TableCell>
                                                <TableCell>{item.name}</TableCell>
                                                <TableCell>{item.description || '-'}</TableCell>
                                                <TableCell align="center">
                                                    {canEditSettings && (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleOpenSubordinateLevelDialog(category, key, true, index, item)}
                                                                title={t('settings.subordinateLevels.actions.edit')}
                                                                id={`edit-${key}-${index}`}
                                                                name={`edit-${key}-${index}`}
                                                                data-testid={`settings-button-edit-${key}-${index}`}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleOpenDeleteSubordinateLevelDialog(category, key, index, item)}
                                                                title={t('settings.subordinateLevels.actions.delete')}
                                                                id={`delete-${key}-${index}`}
                                                                name={`delete-${key}-${index}`}
                                                                data-testid={`settings-button-delete-${key}-${index}`}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <SettingsIcon fontSize="small" />
                        <Typography variant="body2">{t('settings.breadcrumb.system')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('settings.breadcrumb.settings')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Save Button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('settings.title')}
                    </Typography>

                    {/* Save Button */}
                    {canEditSettings && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={loading}
                            id="saveSettings"
                            name="saveSettings"
                            data-testid="settings-button-saveSettings"
                        >
                            {t('settings.save')}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', backgroundColor: '#ffffff', mt: 2 }}
            >
                <Box sx={{ px: 3, pt: 2, pb: 3 }}>
                    {settings.length === 0 && !loading && (
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            {t('settings.noData')}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {settings.map((categoryData, index) => (
                            <Box key={categoryData.category} sx={{ width: 'calc(50% - 8px)', minWidth: 'calc(50% - 8px)' }}>
                                <Accordion
                                    expanded={expandedPanel === categoryData.category}
                                    onChange={handleAccordionChange(categoryData.category)}
                                    sx={{ mb: 0, '&:before': { display: 'none' } }}
                                    elevation={1}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon sx={{ color: '#ffffff' }} />}
                                        sx={{
                                            backgroundColor: '#028661',
                                            borderBottom: '1px solid #e5e7eb',
                                            '&:hover': { backgroundColor: '#03a075' },
                                            color: '#ffffff',
                                        }}
                                    >
                                        <Typography variant="h6" fontWeight="medium" sx={{ color: '#ffffff' }}>
                                            {t(categoryData.category)}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 3 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left' }}>
                                            {categoryData.settings.map((setting) => (
                                                <Box key={setting.key} sx={{ textAlign: 'left' }}>
                                                    {renderSettingInput(categoryData.category, setting)}
                                                </Box>
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Paper>
            <Dialog
                open={passwordDialogOpen}
                onClose={handleClosePasswordDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{t('settings.confirmation.title')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('settings.confirmation.description')}
                    </Typography>
                    <Input
                        label={t('settings.confirmation.passwordLabel')}
                        type="password"
                        value={passwordValue}
                        onChange={(e) => {
                            setPasswordValue(e.target.value);
                            if (passwordError) {
                                setPasswordError('');
                            }
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleConfirmSaveWithPassword();
                            }
                        }}
                        error={!!passwordError}
                        helperText={passwordError}
                        autoFocus
                        disabled={loading}
                        data-testid="settings-password-confirmation"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleClosePasswordDialog}
                        disabled={loading}
                        data-testid="settings-password-cancel"
                    >
                        {t('settings.confirmation.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConfirmSaveWithPassword}
                        disabled={loading}
                        data-testid="settings-password-confirm"
                    >
                        {t('settings.confirmation.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Subordinate Level Add/Edit Dialog */}
            <Dialog
                open={subordinateLevelDialogOpen}
                onClose={handleCloseSubordinateLevelDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}>
                    {subordinateLevelEditMode ? <EditIcon /> : <AddIcon />}
                    {subordinateLevelEditMode
                        ? t('settings.subordinateLevels.editDialog.title')
                        : t('settings.subordinateLevels.addDialog.title')
                    }
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                        <Input
                            label={t('settings.subordinateLevels.form.code')}
                            value={subordinateLevelFormData.code}
                            onChange={handleSubordinateLevelFormChange('code')}
                            required
                            autoFocus={!subordinateLevelEditMode}
                            disabled={subordinateLevelEditMode}
                            id="subordinate-level-code"
                            name="subordinate-level-code"
                            data-testid="settings-subordinate-level-input-code"
                        />
                        <Input
                            label={t('settings.subordinateLevels.form.name')}
                            value={subordinateLevelFormData.name}
                            onChange={handleSubordinateLevelFormChange('name')}
                            required
                            autoFocus={subordinateLevelEditMode}
                            id="subordinate-level-name"
                            name="subordinate-level-name"
                            data-testid="settings-subordinate-level-input-name"
                        />
                        <Input
                            label={t('settings.subordinateLevels.form.description')}
                            value={subordinateLevelFormData.description}
                            onChange={handleSubordinateLevelFormChange('description')}
                            multiline
                            rows={3}
                            id="subordinate-level-description"
                            name="subordinate-level-description"
                            data-testid="settings-subordinate-level-input-description"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseSubordinateLevelDialog}
                        variant="outlined"
                        id="cancel-subordinate-level"
                        name="cancel-subordinate-level"
                        data-testid="settings-subordinate-level-button-cancel"
                    >
                        {t('settings.subordinateLevels.form.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmSubordinateLevel}
                        variant="contained"
                        disabled={!isSubordinateLevelFormValid()}
                        startIcon={subordinateLevelEditMode ? <EditIcon /> : <AddIcon />}
                        id="confirm-subordinate-level"
                        name="confirm-subordinate-level"
                        data-testid="settings-subordinate-level-button-confirm"
                    >
                        {subordinateLevelEditMode
                            ? t('settings.subordinateLevels.form.update')
                            : t('settings.subordinateLevels.form.create')
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Subordinate Level Delete Dialog */}
            <Dialog
                open={subordinateLevelDeleteDialogOpen}
                onClose={handleCloseDeleteSubordinateLevelDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
                    <DeleteIcon />
                    {t('settings.subordinateLevels.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {t('settings.subordinateLevels.deleteDialog.message', {
                            name: subordinateLevelToDelete?.item?.name || ''
                        })}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseDeleteSubordinateLevelDialog}
                        variant="outlined"
                        id="cancel-delete-subordinate-level"
                        name="cancel-delete-subordinate-level"
                        data-testid="settings-subordinate-level-button-delete-cancel"
                    >
                        {t('settings.subordinateLevels.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDeleteSubordinateLevel}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirm-delete-subordinate-level"
                        name="confirm-delete-subordinate-level"
                        data-testid="settings-subordinate-level-button-delete-confirm"
                    >
                        {t('settings.subordinateLevels.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Settings;
